<script lang="ts" setup>
import { onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useQuasar } from 'quasar'
import type { NotificationCategory } from '@roadfootage/api-client'
import { useNotificationPreferencesStore } from '../../stores/notification-preferences'

const $q = useQuasar()
const store = useNotificationPreferencesStore()
const { preferences, loading, loadFailed } = storeToRefs(store)

function notifyFailure(message: string) {
  $q.notify({ color: 'negative', textColor: 'white', icon: 'mdi-alert', message })
}

async function onToggle(category: NotificationCategory, enabled: boolean) {
  const result = await store.setEmailEnabled(category, enabled)
  if (!result.ok) notifyFailure(result.message)
}

async function onInAppToggle(category: NotificationCategory, enabled: boolean) {
  const result = await store.setInAppEnabled(category, enabled)
  if (!result.ok) notifyFailure(result.message)
}

onMounted(() => store.load())
</script>

<template>
  <q-card flat bordered id="notifications" class="settings-card" data-testid="notifications-card">
    <q-card-section>
      <div class="text-h6">Notifications</div>
      <div class="text-caption text-grey-7">Email · In app</div>
    </q-card-section>

    <q-card-section v-if="loading && preferences.length === 0" class="text-center">
      <q-spinner color="primary" size="24px" />
    </q-card-section>
    <q-card-section v-else-if="loadFailed" class="column items-start q-gutter-sm">
      <div>We could not load your notification settings.</div>
      <q-btn outline label="Try again" @click="store.load()" />
    </q-card-section>

    <q-list role="list" v-else separator>
      <q-item
        v-for="pref in preferences"
        :key="pref.category"
        :data-testid="`notif-row-${pref.category}`"
      >
        <q-item-section>
          <q-item-label>{{ pref.displayName }}</q-item-label>
          <q-item-label caption>
            {{ pref.isTransactional ? 'Always sent' : pref.description }}
          </q-item-label>
        </q-item-section>
        <q-item-section side>
          <div class="row no-wrap items-center">
            <q-toggle
              :model-value="pref.emailEnabled"
              :disable="pref.isTransactional"
              :aria-label="`${pref.displayName} emails`"
              :data-testid="`notif-toggle-${pref.category}`"
              @update:model-value="(value: boolean) => onToggle(pref.category!, value)"
            />
            <q-toggle
              v-if="pref.inAppAvailable"
              :model-value="pref.inAppEnabled"
              :disable="pref.isTransactional"
              :aria-label="`${pref.displayName} in the app`"
              :data-testid="`notif-inapp-toggle-${pref.category}`"
              @update:model-value="(value: boolean) => onInAppToggle(pref.category!, value)"
            />
            <div v-else class="notif-toggle-gap" aria-hidden="true" />
          </div>
        </q-item-section>
      </q-item>
    </q-list>
  </q-card>
</template>

<style scoped>
/* Keeps the email toggles in one column when a category has no in-app toggle. */
.notif-toggle-gap {
  width: 40px;
}
</style>
